// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState } from "react";
import { useVaccineDashboardStore } from "../../../store/vaccineDashboardStore";
import { useVaccineClientStore } from "../../../store/vaccineClientStore";
import { formatEurosCompact, formatPercentage } from "../lib/models";
import { ConfirmationDialog } from "./ConfirmationDialog";

interface ScenarioManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ScenarioManagerModal: React.FC<ScenarioManagerModalProps> = ({
  isOpen,
  onClose,
}) => {
  const scenarios = useVaccineDashboardStore((state) => state.scenarios);
  const loadScenario = useVaccineDashboardStore((state) => state.loadScenario);
  const deleteScenario = useVaccineDashboardStore((state) => state.deleteScenario);
  
  // Decision Tree Store
  const setNodes = useVaccineClientStore((state) => state.setNodes);
  const setEdges = useVaccineClientStore((state) => state.setEdges);
  const setRootId = useVaccineClientStore((state) => state.setRootId);

  const [pendingLoad, setPendingLoad] = useState<any | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  if (!isOpen) return null;

  const handleLoad = (scenario: any) => {
    setPendingLoad(scenario);
  };

  const confirmLoad = () => {
    const scenario = pendingLoad;
    if (scenario) {
      loadScenario(scenario);

      // Load decision tree if available
      if (scenario.decisionTree) {
        setNodes(scenario.decisionTree.nodes);
        setEdges(scenario.decisionTree.edges);
        setRootId(scenario.decisionTree.rootId);
      }

      onClose();
    }
    setPendingLoad(null);
  };

  const handleDelete = (id: string, name: string) => {
    setPendingDelete({ id, name });
  };

  const confirmDelete = () => {
    if (pendingDelete) {
      deleteScenario(pendingDelete.id);
    }
    setPendingDelete(null);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="scenario-manager-title"
        data-cy="saved-scenarios-modal"
        style={{
          background: "#1e293b",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: "800px",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          border: "1px solid #334155",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h2 id="scenario-manager-title" style={{ margin: 0, fontSize: "20px", fontWeight: 600, color: "#f1f5f9" }}>
            Saved Scenarios ({scenarios.length})
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            data-cy="close-scenarios"
            style={{
              background: "transparent",
              border: "none",
              color: "#94a3b8",
              cursor: "pointer",
              fontSize: "24px",
              padding: "4px",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "12px" }}>
          {scenarios.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8", fontStyle: "italic" }}>
              No saved scenarios — save the current setup to create one
            </div>
          ) : (
            scenarios.map((scenario) => (
              <div
                key={scenario.id}
                data-cy="scenario-card"
                style={{
                  background: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid #334155",
                  borderRadius: "12px",
                  padding: "16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                    <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#f1f5f9" }}>
                      {scenario.name}
                    </h3>
                    <span style={{ 
                      fontSize: "11px", 
                      padding: "2px 6px", 
                      borderRadius: "4px", 
                      background: "rgba(59, 130, 246, 0.1)", 
                      color: "#60a5fa",
                      border: "1px solid rgba(59, 130, 246, 0.2)"
                    }}>
                      {scenario.disease}
                    </span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#94a3b8", display: "flex", gap: "12px" }}>
                    <span>{new Date(scenario.timestamp).toLocaleString()}</span>
                  </div>
                  
                  {/* Key Metrics Preview */}
                  <div style={{ display: "flex", gap: "16px", marginTop: "12px" }}>
                    <MetricPreview label="ICER" value={formatEurosCompact(scenario.outputs.icer)} />
                    <MetricPreview label="QALYs" value={scenario.outputs.incremental.qalyGained.toFixed(0)} />
                    <MetricPreview label="Budget" value={formatEurosCompact(scenario.outputs.budgetImpact)} />
                    <MetricPreview label="Cov." value={formatPercentage(scenario.inputs.coveragePctByRegion['finland'] || 0)} />
                  </div>
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => handleLoad(scenario)}
                    className="btn btn-secondary btn-sm"
                    style={{ borderColor: "#3b82f6", color: "#3b82f6" }}
                  >
                    Load
                  </button>
                  <button
                    onClick={() => handleDelete(scenario.id, scenario.name)}
                    className="btn btn-secondary btn-sm"
                    style={{ borderColor: "#ef4444", color: "#ef4444" }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #334155", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} className="btn btn-secondary" aria-label="Close" data-cy="close-scenarios">
            Close
          </button>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={pendingLoad !== null}
        title="Load scenario?"
        message={`Load scenario "${pendingLoad?.name ?? ''}"? Unsaved changes will be lost.`}
        confirmText="Load"
        cancelText="Cancel"
        variant="warning"
        onConfirm={confirmLoad}
        onCancel={() => setPendingLoad(null)}
      />

      <ConfirmationDialog
        isOpen={pendingDelete !== null}
        title="Delete scenario?"
        message={`Delete scenario "${pendingDelete?.name ?? ''}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

const MetricPreview = ({ label, value }: { label: string; value: string }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
    <span style={{ fontSize: "10px", color: "#64748b", textTransform: "uppercase" }}>{label}</span>
    <span style={{ fontSize: "13px", fontWeight: 600, color: "#e2e8f0" }}>{value}</span>
  </div>
);
